import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, collection, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import AuthContext, { UserData } from '../contexts/AuthContext';
import ProfileHeader from '../components/profile/ProfileHeader';
import ProfileTabs from '../components/profile/ProfileTabs';
import PostCard from '../components/posts/PostCard';
import SEOHead from '../components/seo/SEOHead';
import StructuredData from '../components/seo/StructuredData';
import { followUser, unfollowUser, listenToFollowStatus } from '../services/followService';
import DirectLikedPosts from '../components/profile/DirectLikedPosts';

// Importar funciones de URL
const getFullUrl = (path: string) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}${path}`;
};

const getUserProfileUrl = (userId: string, displayName: string) => {
  const slug = displayName
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '-');
  return `/profile/${userId}/${slug}`;
};

interface Post {
  id: string;
  userId: string;
  userName: string;
  userPhotoURL: string;
  content: string;
  imageUrl?: string;
  videoUrl?: string;
  likes: number;
  comments: number;
  createdAt: Timestamp | Date | null;
}

const Profile: React.FC = () => {
  // Obtener parámetros de URL (tanto para rutas tradicionales como amigables para SEO)
  const { userId, userSlug } = useParams<{ userId?: string, userSlug?: string }>();
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();

  // Determinar el ID de usuario a buscar (desde userId directo o buscando por slug)
  const [targetUserId, setTargetUserId] = useState<string | undefined>(userId);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'posts' | 'media' | 'likes'>('posts');
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  // Agregar CSS global para ocultar los IDs
  useEffect(() => {
    // Crear un elemento de estilo
    const style = document.createElement('style');
    style.textContent = `
      [id^="firebase"], [class^="firebase"], [data-userid], .user-id {
        display: none !important;
      }
    `;
    // Agregar el estilo al head del documento
    document.head.appendChild(style);

    // Limpiar al desmontar
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Verificar si el usuario actual sigue al usuario del perfil
  useEffect(() => {
    if (!currentUser || !targetUserId || currentUser.uid === targetUserId) {
      // Si es el propio perfil, no necesitamos verificar el estado de seguimiento
      return;
    }

    // Usar el servicio de seguimiento para escuchar cambios en el estado de seguimiento
    const unsubscribe = listenToFollowStatus(
      currentUser.uid,
      targetUserId!, // Aseguramos que targetUserId existe en este punto
      (isFollowing) => {
        setIsFollowing(isFollowing);
        console.log(`Estado de seguimiento actualizado: ${isFollowing ? 'Siguiendo' : 'No siguiendo'}`);
      }
    );

    // Limpiar el listener cuando el componente se desmonte
    return () => unsubscribe();
  }, [currentUser, targetUserId]);

  // Ya no necesitamos este efecto ya que LikedPostsTab maneja su propia carga de datos
  // useEffect(() => {
  //   // Código eliminado que cargaba los posts con "Me gusta"
  // }, [activeTab, targetUserId]);

  // Eliminar cualquier visualización del ID de usuario
  useEffect(() => {
    // Eliminar cualquier elemento que pueda mostrar el ID de usuario
    const removeUserIds = () => {
      // Buscar y eliminar cualquier texto que parezca un ID de Firebase
      const textNodes = document.createTreeWalker(
        document.body,
        NodeFilter.SHOW_TEXT,
        null
      );

      let node;
      while ((node = textNodes.nextNode()) !== null) {
        const text = node.nodeValue || '';
        // Buscar patrones que parezcan IDs de Firebase (cadenas largas alfanuméricas)
        if (/[a-zA-Z0-9]{20,}/.test(text)) {
          node.nodeValue = '';
        }
      }
    };

    // Ejecutar después de que el componente se monte y cuando cambie el userId
    removeUserIds();

    // También configurar un observador para detectar cambios en el DOM
    const observer = new MutationObserver(removeUserIds);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [targetUserId]);

  // Buscar usuario por slug si es necesario
  useEffect(() => {
    // Si tenemos un userSlug pero no un userId, necesitamos buscar el usuario por su slug
    if (userSlug && !userId) {
      setLoading(true);

      // Buscar en la colección de usuarios por displayName que coincida con el slug
      const findUserBySlug = async () => {
        try {
          const usersRef = collection(db, 'users');
          const q = query(usersRef);
          const querySnapshot = await getDocs(q);

          let foundUser = false;

          // Buscar entre todos los usuarios (esto podría optimizarse con un índice)
          for (const doc of querySnapshot.docs) {
            const userData = doc.data();
            const generatedSlug = userData.displayName
              ?.toLowerCase()
              .replace(/[^\w\s]/gi, '')
              .replace(/\s+/g, '-')
              .replace(/-+/g, '-')
              .replace(/^-+|-+$/g, '');

            if (generatedSlug === userSlug) {
              setTargetUserId(doc.id);
              foundUser = true;
              break;
            }
          }

          if (!foundUser) {
            setError('Usuario no encontrado');
            setLoading(false);
          }
        } catch (error) {
          console.error('Error buscando usuario por slug:', error);
          setError('Error al buscar usuario');
          setLoading(false);
        }
      };

      findUserBySlug();
    }
  }, [userSlug, userId]);

  // Cargar datos del usuario y publicaciones con listener en tiempo real
  useEffect(() => {
    if (!targetUserId) {
      if (!userSlug) { // Solo mostrar error si no estamos buscando por slug
        setError('ID de usuario no válido');
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setError(null);

    // Listener en tiempo real para datos del usuario (para actualizar followers/following en tiempo real)
    const userDocRef = doc(db, 'users', targetUserId!);
    const unsubscribeUser = onSnapshot(userDocRef, (userDocSnap) => {
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data() as UserData;
        setUserData(userData);
        setLoading(false);
      } else {
        setError('Usuario no encontrado');
        setLoading(false);
      }
    }, (error) => {
      console.error('Error en listener de usuario:', error);
      setError('Error al cargar el perfil');
      setLoading(false);
    });

    // Cargar posts del usuario (una sola vez, no necesita tiempo real)
    const loadPosts = async () => {
      try {
        const postsQuery = query(
          collection(db, 'posts'),
          where('userId', '==', targetUserId),
          orderBy('createdAt', 'desc')
        );

        const postsSnapshot = await getDocs(postsQuery);
        const postsData: Post[] = [];

        postsSnapshot.forEach((docSnap) => {
          postsData.push({
            id: docSnap.id,
            ...docSnap.data() as Omit<Post, 'id'>
          });
        });

        setPosts(postsData);
      } catch (error) {
        console.error('Error cargando posts del perfil:', error);
      }
    };

    loadPosts();

    // Cleanup: desuscribirse cuando el componente se desmonte o cambie targetUserId
    return () => unsubscribeUser();
  }, [targetUserId, userSlug]);


  // Función para seguir/dejar de seguir al usuario
  const toggleFollow = async () => {
    if (!currentUser || !userData || followLoading) {
      return;
    }

    // Activar estado de carga
    setFollowLoading(true);
    console.log(`Intentando ${isFollowing ? 'dejar de seguir' : 'seguir'} a ${userData.displayName}`);

    try {
      if (isFollowing) {
        // Dejar de seguir al usuario
        console.log('Ejecutando unfollowUser');
        await unfollowUser(currentUser.uid, userData.uid);
        console.log('Usuario dejado de seguir con éxito');
      } else {
        // Seguir al usuario
        console.log('Ejecutando followUser');
        await followUser(
          currentUser.uid,
          userData.uid,
          {
            displayName: currentUser.displayName || 'Usuario',
            photoURL: currentUser.photoURL
          },
          {
            displayName: userData.displayName || 'Usuario',
            photoURL: userData.photoURL
          }
        );
        console.log('Usuario seguido con éxito');
      }
    } catch (error) {
      console.error('Error toggling follow status:', error);
      alert('Error al actualizar estado de seguimiento. Inténtalo de nuevo.');
    } finally {
      // Desactivar estado de carga
      setFollowLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-domired-600 mb-4"></div>
        <p className="text-gray-500 dark:text-gray-400">Cargando perfil...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
        <h2 className="text-2xl font-bold text-gray-700 dark:text-gray-300">Error</h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2">{error}</p>
        <button
          onClick={() => navigate('/')}
          className="mt-4 px-4 py-2 bg-domired-600 text-white rounded-md hover:bg-domired-700"
        >
          Volver al inicio
        </button>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
        <h2 className="text-2xl font-bold text-gray-700 dark:text-gray-300">Usuario no encontrado</h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2">
          El perfil que estás buscando no existe o ha sido eliminado.
        </p>
        <button
          onClick={() => navigate('/')}
          className="mt-4 px-4 py-2 bg-domired-600 text-white rounded-md hover:bg-domired-700"
        >
          Volver al inicio
        </button>
      </div>
    );
  }

  // Filtrar posts para las pestañas de posts y media (la pestaña de likes ahora usa LikedPostsTab)
  const filteredPosts = activeTab === 'posts'
    ? posts
    : activeTab === 'media'
      ? posts.filter(post => post.imageUrl || post.videoUrl)
      : [];

  if (!userData) {
    return (
      <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
        <h2 className="text-2xl font-bold text-gray-700 dark:text-gray-300">Error al cargar el perfil</h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2">No se pudieron cargar los datos del usuario.</p>
        <button
          onClick={() => navigate('/')}
          className="mt-4 px-4 py-2 bg-domired-600 text-white rounded-md hover:bg-domired-700"
        >
          Volver al inicio
        </button>
      </div>
    );
  }

  // Preparar datos para SEO
  const profileUrl = getFullUrl(getUserProfileUrl(userData.uid, userData.displayName || ''));
  const profileImage = userData.photoURL || 'https://domired.top/logo.png';
  const profileDescription = userData.bio
    ? `Perfil de ${userData.displayName} en Domired. ${userData.bio.substring(0, 150)}`
    : `Perfil público de ${userData.displayName} en Domired. Conéctate, comparte y descubre más.`;

  return (
    <div className="max-w-3xl mx-auto">
      {/* SEO Metadata */}
      <SEOHead
        title={`${userData.displayName} - Domired`}
        description={profileDescription}
        image={profileImage}
        url={profileUrl}
        type="profile"
      />

      {/* Structured Data for Person */}
      <StructuredData
        type="Person"
        data={{
          name: userData.displayName,
          url: profileUrl,
          image: profileImage,
          description: userData.bio || `Perfil de ${userData.displayName} en Domired`
        }}
      />

      <ProfileHeader
        userData={userData}
        isFollowing={isFollowing}
        toggleFollow={toggleFollow}
        isCurrentUser={currentUser?.uid === userData?.uid}
      />

      <div className="mt-6">
        <ProfileTabs activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>

      <div className="mt-4 space-y-4">
        {activeTab === 'likes' ? (
          // Usar el nuevo componente LikedPostsTab para la pestaña de likes
          <DirectLikedPosts userId={targetUserId || ''} />
        ) : filteredPosts.length > 0 ? (
          // Renderizar cada post con manejo de errores para las otras pestañas
          filteredPosts.map((post: Post) => {
            // Verificar que el post tenga todos los campos necesarios
            if (!post || !post.id || !post.userId) {
              return null; // No renderizar posts inválidos
            }

            try {
              return <PostCard key={post.id} post={post} />;
            } catch (error) {
              console.error(`Error al renderizar post ${post.id}:`, error);
              return null; // Si hay un error al renderizar, no mostrar el post
            }
          })
        ) : (
          <div className="text-center py-10 bg-white dark:bg-gray-800 rounded-lg shadow">
            <p className="text-gray-500 dark:text-gray-400">
              {activeTab === 'posts'
                ? 'No hay publicaciones para mostrar.'
                : activeTab === 'media'
                  ? 'No hay publicaciones con imágenes o videos.'
                  : ''}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
